/**
 * The door list as a bottom sheet — opened by a stat tile (on that scope) or by
 * BY NAME (on `left`). Scope pills, a server-side name search, infinite scroll,
 * and CHECK IN on anyone not fully through the door. Tapping the face or the
 * name opens their Portal Page.
 */
import React, { useEffect } from 'react';
import { SCOPES, rosterMeta } from './doorKinds';
import './door.css';

const EMPTY = {
  padding: 20, borderRadius: 18, background: '#F5F5F5', textAlign: 'center',
  fontSize: 11, fontWeight: 700, letterSpacing: '.14em', color: '#9CA3AF',
};

export default function DoorRosterSheet({
  kind, scope, onScope, onClose, total, rows, search, onSearch, loading, hasMore, sentinelRef,
  onCheckIn, busyId, onOpenGuest,
}) {
  // Esc closes, like every other sheet.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const label = kind.stats[SCOPES.indexOf(scope)] || '';

  return (
    <div
      className="door-root"
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${label} list`}
        className="door-sheet"
        onClick={(e) => e.stopPropagation()}
        data-testid="door-roster"
        style={{
          width: '100%', maxWidth: 560, maxHeight: '86vh', background: '#fff', color: '#000', borderRadius: '28px 28px 0 0',
          display: 'flex', flexDirection: 'column', paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <div style={{ padding: '20px 20px 12px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.16em' }}>{label} · {total}</span>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              style={{ width: 36, height: 36, border: 'none', borderRadius: '50%', background: '#F2F2F2', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19" /></svg>
            </button>
          </span>
          <div style={{ display: 'flex', gap: 6 }} role="tablist">
            {SCOPES.map((s, i) => {
              const on = s === scope;
              return (
                <button
                  key={s}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => onScope(s)}
                  style={{
                    flex: 1, height: 38, border: 'none', borderRadius: 999, background: on ? '#000' : '#F2F2F2', color: on ? '#fff' : '#000',
                    fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '.14em', cursor: 'pointer', transition: 'background-color .2s, color .2s',
                  }}
                >
                  {kind.stats[i]}
                </button>
              );
            })}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: 44, padding: '0 16px', borderRadius: 999, background: '#F2F2F2' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M21 21l-5-5" /></svg>
            <input
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="Search by name"
              aria-label="Search by name"
              style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 12 }}
            />
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px 24px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rows.length === 0 && (
            <span style={EMPTY}>{loading ? 'LOOKING…' : 'NOBODY HERE.'}</span>
          )}
          {rows.map((g) => {
            const qty = Math.max(1, g.quantity || 1);
            const c = g.checkedInCount ?? (g.checkedInAt ? qty : 0);
            const done = c >= qty;
            const canOpen = !!(g.handle || g.guestUserId);
            const open = canOpen ? () => onOpenGuest(g) : undefined;
            return (
              <div key={g.applicationId} data-testid={`roster-row-${g.applicationId}`} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 8, borderRadius: 20, background: '#F5F5F5' }}>
                <button
                  type="button"
                  onClick={open}
                  disabled={!canOpen}
                  aria-label={canOpen ? `Open ${g.guestName}'s Portal Page` : undefined}
                  style={{ width: 44, height: 44, borderRadius: 14, overflow: 'hidden', flexShrink: 0, background: '#E5E5E5', border: 'none', padding: 0, cursor: canOpen ? 'pointer' : 'default', fontFamily: 'inherit', fontSize: 14, fontWeight: 700 }}
                >
                  {g.avatarUrl
                    ? <img src={g.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    : (g.guestName || '?').charAt(0).toUpperCase()}
                </button>
                <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3, textAlign: 'left' }}>
                  {canOpen ? (
                    <button type="button" onClick={open} style={{ all: 'unset', cursor: 'pointer', fontSize: 13, fontWeight: 700, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                      {g.guestName}
                    </button>
                  ) : (
                    <span style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{g.guestName}</span>
                  )}
                  <span style={{ fontSize: 10, letterSpacing: '.1em', color: '#6B7280' }}>{rosterMeta(kind, g)}</span>
                </span>
                {done ? (
                  <span style={{ height: 36, padding: '0 14px', borderRadius: 999, background: '#03FF00', display: 'flex', alignItems: 'center', fontSize: 10, fontWeight: 700, letterSpacing: '.14em', flexShrink: 0 }}>IN</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => onCheckIn(g)}
                    disabled={busyId === g.applicationId}
                    style={{ height: 36, padding: '0 14px', border: 'none', borderRadius: 999, background: '#000', color: '#fff', fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '.14em', cursor: 'pointer', flexShrink: 0, opacity: busyId === g.applicationId ? 0.6 : 1 }}
                  >
                    {busyId === g.applicationId ? 'CHECKING…' : 'CHECK IN'}
                  </button>
                )}
              </div>
            );
          })}
          {/* Keeps pulling the next page as you scroll (no load-more buttons). */}
          <div ref={sentinelRef} style={{ height: 4, flexShrink: 0 }} />
          {rows.length > 0 && loading && hasMore && (
            <span style={{ padding: 8, textAlign: 'center', fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: '#9CA3AF' }}>LOADING MORE…</span>
          )}
        </div>
      </div>
    </div>
  );
}
